import {
    AbstractPmsApiService
} from "./abstract.service";
import {
    PropertyAccommodationType
} from "../models";
import {
    PaginationCollection
} from "../models/pagination-collection.model";

/**
 * @class PropertyAccommodationTypePmsApiService
 * @abstract
 * @extends {AbstractPmsApiService<PropertyAccommodationType>}
 */
export class PropertyAccommodationTypePmsApiService extends AbstractPmsApiService {

    /**
     * @type {string}
     * @protected
     */
    get urlPattern() {

        return '/property/:propertyId/accommodation-type(/:id)';
    }

    /**
     * @param {Object.<string, *>} attributes
     * @param {...*} params
     * @returns {PropertyAccommodationType}
     * @protected
     */
    newModel(attributes, ...params) {

        return new PropertyAccommodationType(attributes);
    }

    /**
     * @param {Object.<string, *>} paginationData
     * @param {...*} params
     * @returns {PaginationCollection<M>}
     * @protected
     */
    newPaginationCollection(paginationData, ...params) {

        const totalRow = paginationData.data.total_row;
        paginationData.data = Object.values(paginationData.data);
        const lastItem = paginationData.data.pop();
        if (lastItem && !lastItem.hasOwnProperty('total_occupation')) {
            paginationData.data.push(lastItem);
        }

        return new PaginationCollection({
            perPage: paginationData.per_page,
            page: paginationData.current_page,
            total: paginationData.total,
            total_row: totalRow,
            from: paginationData.from,
            to: (paginationData.to - 1) || 0,
            models: paginationData.data.map(
                (attributes) => this.newModel(attributes, ...params))
        });
    }

    /**
     * @param {Object=} params
     * @param {Property=} property
     * @returns {Promise.<PaginationCollection<PropertyAccommodationType>>}
     */
    get(params = {}, property = null) {

        const url = this.url({
            propertyId: property ? property.id : 0
        });

        return this.http
            .get(url, {
                headers: this.authHeaders,
                params
            })
            .then(this.getPaginationCollector())
            .catch(this.getErrorHandler());
    }

    /**
     * @param {PropertyAccommodationType} propertyAccommodationType
     * @param {Property} property
     * @returns {Promise.<PropertyAccommodationType>}
     */
    create(propertyAccommodationType, property) {

        const url = this.url({
            propertyId: property.id
        });

        return this.http
            .post(url, propertyAccommodationType.toObjectExcept('property'), {
                headers: this.authHeaders
            })
            .then(this.getModeler())
            .catch(this.getErrorHandler());
    }

    /**
     * @param {PropertyAccommodationType} propertyAccommodationType
     * @returns {Promise.<PropertyAccommodationType>}
     */
    update(propertyAccommodationType) {

        const url = this.url({
            propertyId: propertyAccommodationType.property.id,
            id: propertyAccommodationType.id
        });

        return this.http
            .put(url, propertyAccommodationType.toObjectExcept('property'), {
                headers: this.authHeaders
            })
            .then(this.getModeler())
            .catch(this.getErrorHandler());
    }

    /**
     * @param {PropertyAccommodationType} propertyAccommodationType
     * @returns {Promise.<PropertyAccommodationType>}
     */
    ntmpMapping(propertyAccommodationType) {

        const url = this.url({
            propertyId: propertyAccommodationType.property.id,
            id: propertyAccommodationType.id
        });

        return this.http
            .put(url + '/ntmp-mapping', propertyAccommodationType.toObjectExcept('property'), {
                headers: this.authHeaders
            })
            .then(this.getModeler())
            .catch(this.getErrorHandler());
    }

    bulkUpdate(property, data) {

        const url = this.url({
            propertyId: property.id
        });

        return this.http
            .post(url + '/bulk-update', data, {
                headers: this.authHeaders
            })
            .then(this.getModeler())
            .catch(this.getErrorHandler());
    }

    /**
     * @param {PropertyAccommodationType} propertyAccommodationType
     * @returns {Promise.<boolean>}
     */
    delete(propertyAccommodationType) {

        const url = this.url({
            propertyId: propertyAccommodationType.property.id,
            id: propertyAccommodationType.id
        });

        return this.http
            .delete(url, {
                headers: this.authHeaders
            })
            .then(this.getStatusExtractor())
            .catch(this.getErrorHandler());
    }

    /**
     * @param {Property} property
     * @param Array types
     * @returns {Promise.<boolean>}
     */
    reorder(property, types) {

        const url = this.url({
            propertyId: property.id
        });

        return this.http
            .post(url + '/reorder', {
                'types': types
            }, {
                headers: this.authHeaders
            })
            .then(this.getStatusExtractor())
            .catch(this.getErrorHandler());
    }

    availableRooms(params = {}, property) {

        const url = this.url({propertyId: property.id}) + '/available-rooms';

        return this.http
            .post(url, params, {
                headers: this.authHeaders
            })
            .then(this.getDataExtractor())
            .catch(this.getErrorHandler());
    }

    freeRooms(accommodation, property, reservationId) {

        const url = this.url({
            propertyId: property.id,
            id: accommodation.id
        }) + `/reservation/${reservationId}/free-rooms`;

        return this.http
            .get(url, {
                headers: this.authHeaders
            })
            .then(this.getDataExtractor())
            .catch(this.getErrorHandler());
    }

    loadAvailableAccommodationTypes(propertyID, params = {}) {

        const url = this.url({}, `/property/${propertyID}/accommodations-availability`);

        params['load_rate_plans'] = angular.isDefined(params['load_rate_plans']) ? params.load_rate_plans : 1;

        return this.http.get(url, {
            headers: this.authHeaders,
            params
        })
            .then(this.getDataExtractor())
            .catch(this.getErrorHandler());
    }

    findFutureAccommodations(accommodationType) {

        const url = this.url({
            propertyId: accommodationType.property.id,
            id: accommodationType.id
        }) + '/find-future-accommodations';

        return this.http.get(url, {
            headers: this.authHeaders
        }).then(this.getDataExtractor());
    }

    checkReservationAssignment(accommodationType) {

        const url = this.url({
            propertyId: accommodationType.property.id,
            id: accommodationType.id
        }) + '/check-reservation-assignment';

        return this.http
            .get(url, {
                headers: this.authHeaders
            })
            .then(this.getModeler())
            .catch(this.getErrorHandler());
    }

    maxCountRooms(id) {

        const url = this.url({
            propertyId: id,
            id: 0
        }) + '/property-max-count-rooms';

        return this.http
            .get(url, {
                headers: this.authHeaders
            })
            .then(this.getDataExtractor())
            .catch(this.getErrorHandler());
    }
}



// WEBPACK FOOTER //
// ./src/pms-api/services/property-accommodation-type.service.js